import React from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatSchedule, intervalChoices, parseSchedule, ScheduleFields, ScheduleMode, weekdays } from './cronSchedule';

type Props = { id: string; label: string; value: string; disabled: boolean; onChange: (value: string) => void };
const modes: { value: ScheduleMode; label: string }[] = [
  { value: 'disabled', label: 'Disabled' },
  { value: 'seconds', label: 'Every few seconds' },
  { value: 'minutes', label: 'Every few minutes' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

export const CronScheduleEditor: React.FC<Props> = ({ id, label, value, disabled, onChange }) => {
  const fields = parseSchedule(value);
  const update = (patch: Partial<ScheduleFields>) => {
    const next = formatSchedule({ ...fields, ...patch });
    if (next !== null) onChange(next);
  };
  const dropdown = (suffix: string, title: string, selected: string, options: { value: string; label: string }[], change: (value: string) => void) => (
    <div className={`shrink-0 space-y-1 ${suffix === 'frequency' ? 'w-44' : 'w-32'}`}>
      <label htmlFor={`${id}-${suffix}`} className={suffix === 'frequency' ? 'sr-only' : 'text-xs font-medium'}>{title}</label>
      <Select value={selected} onValueChange={change} disabled={disabled}>
        <SelectTrigger id={`${id}-${suffix}`} aria-label={`${label} ${title.toLowerCase()}`}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  return (
    <div className="w-max space-y-2">
      <div className="flex items-end gap-2">
      {dropdown('frequency', 'Frequency', fields.mode, [
        ...modes,
        ...(fields.mode === 'custom' ? [{ value: 'custom', label: value ? 'Custom schedule (unchanged)' : 'Choose frequency' }] : []),
      ], mode => update({ mode: mode as ScheduleMode }))}
        {(fields.mode === 'seconds' || fields.mode === 'minutes') && dropdown('interval', 'Repeat every', fields.interval,
          intervalChoices.map(interval => ({ value: interval, label: `${interval} ${fields.mode === 'seconds' ? Number(interval) === 1 ? 'second' : 'seconds' : Number(interval) === 1 ? 'minute' : 'minutes'}` })),
          interval => update({ interval }))}
        {fields.mode === 'weekly' && dropdown('weekday', 'Day', fields.weekday,
          weekdays.map((day, index) => ({ value: String(index), label: day })), weekday => update({ weekday }))}
        {fields.mode === 'monthly' && dropdown('monthday', 'Day of month', fields.monthday,
          Array.from({ length: 31 }, (_, index) => ({ value: String(index + 1), label: String(index + 1) })), monthday => update({ monthday }))}
        {fields.mode === 'hourly' && dropdown('minute', 'Minutes past the hour', fields.time.slice(3),
          Array.from({ length: 60 }, (_, index) => ({ value: String(index).padStart(2, '0'), label: String(index).padStart(2, '0') })), minute => update({ time: `00:${minute}` }))}
        {['daily', 'weekly', 'monthly'].includes(fields.mode) && (
          <div className="w-32 shrink-0 space-y-1">
            <label htmlFor={`${id}-time`} className="sr-only">Time</label>
            <Input id={`${id}-time`} aria-label={`${label} run time`} type="time" value={fields.time} disabled={disabled} onChange={event => update({ time: event.target.value })} />
          </div>
        )}
      </div>
      {fields.mode === 'monthly' && Number(fields.monthday) > 28 && <p className="text-xs text-muted-foreground">Runs only in months containing this day.</p>}
      {fields.mode === 'custom' && value && (
        <p className="text-xs text-muted-foreground">Existing timing is preserved. Choose a frequency to replace it.</p>
      )}
    </div>
  );
};
